import { dateUtils } from 'shared';

import type { SearchPageOpenParams, DetailsPageOpenParams } from '../analytics';
import { extractAnalyticsDataLib } from '../analytics-data-extraction';
import { bloomreachLib } from '../bloomreach';
import { BloomreachEventName } from './config';

export const searchPageOpen = (
  params: SearchPageOpenParams & { minPrice?: number },
) => {
  const mappedData: Record<string, unknown> = {
    ...extractAnalyticsDataLib.searchPageOpen(params),
  };

  if (params.error) {
    mappedData.error = params.error;
  }

  mappedData.search_status = params.success ? 'success' : 'error';
  mappedData.serp_open_source = params.source;
  mappedData.min_offer_price = params.minPrice;

  // When coming from sources other than main page (hot_offers, special_offers),
  // extract city data from dict using searchParams
  if (params.source !== 'main_page') {
    extractAnalyticsDataLib.enrichEventWithCityData(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithPassengerData(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithCabinClass(mappedData, params);
    extractAnalyticsDataLib.enrichEventWithDatesAndSearchMode(
      mappedData,
      params,
    );
    extractAnalyticsDataLib.enrichEventWithTravelType(mappedData, params);

    // Convert dates to Unix for Bloomreach
    if (mappedData.date_from) {
      mappedData.date_from = dateUtils.toUnix(mappedData.date_from as string);
    }
    if (mappedData.date_to) {
      mappedData.date_to = dateUtils.toUnix(mappedData.date_to as string);
    }
  }

  delete mappedData.status;
  delete mappedData.source;
  delete mappedData.search_time;
  delete mappedData.segment_filter_on;
  delete mappedData.partner_filter;
  delete mappedData.calendar_filter_on;
  delete mappedData.business_in_economy_count;

  // When coming from sources other than main page (hot_offers, special_offers),
  // don't collect properties from previous search events to avoid mixing data
  const skipCollectProperties = params.source !== 'main_page';

  bloomreachLib.track(
    BloomreachEventName.SERP_PAGE_OPEN,
    mappedData as Parameters<typeof bloomreachLib.track>[1],
    {
      skipCollectProperties,
    },
  );
};

export const offerPriceClick = (params: DetailsPageOpenParams) => {
  const parsedOfferData = extractAnalyticsDataLib.offer(params.offer);

  const segmentsData = extractAnalyticsDataLib.getSegmentsDataFromOffer(
    params.offer,
    params.dict,
  );

  const isNoBaggageItem = (item: string | null | undefined) =>
    !item || item === '0 PC' || item === '0 KG';

  const hasBaggage = () => {
    if (parsedOfferData.baggage.length === 0) {
      return false;
    }
    return !parsedOfferData.baggage.every(isNoBaggageItem);
  };

  const hasHandLuggage = () => {
    if (parsedOfferData.handLuggage.length === 0) {
      return false;
    }
    return !parsedOfferData.handLuggage.every(isNoBaggageItem);
  };

  const mappedData: Record<string, unknown> = {
    ...segmentsData,
    airlines: parsedOfferData.airlines.map(a => a.title),
    flight_numbers: parsedOfferData.flightsNumbers,
    price: parsedOfferData.price,
    refundable: parsedOfferData.isRefundable,
    direct: !parsedOfferData.hasConnections && !parsedOfferData.hasStops,
    connect_type: params.offer.$meta.connect_type?.toLocaleLowerCase(),
    offer_position: params.index + 1,
    catalog_item_id: (params.offer.$meta as { hash?: string }).hash,
    has_baggage: hasBaggage(),
    has_hand_luggage: hasHandLuggage(),
  };

  bloomreachLib.track(
    BloomreachEventName.OFFER_PRICE_CLICK,
    mappedData as Parameters<typeof bloomreachLib.track>[1],
  );
};
